/**
 * บริบทที่ `DesktopShell` (layout route) ส่งต่อให้หน้าจอลูกผ่าน `<Outlet />`
 *
 * สิทธิ์ถูกโหลดครั้งเดียวที่ shell แล้วแชร์ลงมา ไม่ให้แต่ละหน้าจอยิง
 * `GET /v1/me/permissions` ซ้ำของตัวเอง — นอกจากจะเปลืองแล้ว หน้าจอที่ได้คำตอบ
 * คนละรอบกันยังทำให้เมนูกับเนื้อหาไม่ตรงกันได้
 *
 * ไม่มีการ cache ลง localStorage/sessionStorage โดยเจตนา: permission set ที่ค้าง
 * อยู่ในเบราว์เซอร์คือ allow-list เก่าที่รอสร้างปัญหา
 */

import { useOutletContext } from 'react-router-dom';

import type { Breadcrumb } from './HeaderBar';
import type { PermissionsState } from '../lib/permissions/types';

export interface DesktopOutletContext {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /**
   * ให้หน้าจอลูก contribute breadcrumb ท้ายสุดของตัวเอง (`WO-P2-M11-004`, D-107 ข้อ 6)
   *
   * หน้าจอย่อยของแอปนี้หลายตัวเป็น search param ไม่ใช่ nested route (ดู
   * `ProductMasterScreen`/`resolveProductView`) router เพียงอย่างเดียวจึงไม่รู้ว่า
   * ผู้ใช้อยู่ลึกแค่ไหน — `DesktopShell` จึงเปิดช่องทางนี้ให้หน้าจอบอกเอง แทนที่จะ
   * เดาจาก URL ซึ่งเป็นกลไกที่สองที่ invariant ของ Work Order นี้ห้ามไว้
   *
   * เรียกได้จาก `useEffect` เท่านั้น — เรียกตรงระหว่าง render จะทำให้
   * `DesktopShell` re-render วนไม่จบ
   */
  readonly setTrailingCrumbs: (crumbs: readonly Breadcrumb[]) => void;
  /** ให้หน้าจอลงทะเบียนรายการของตัวเองในเมนู "ตัวเลือก" (`WO-P2-M11-023`, D-142) */
  readonly setHeaderOptionItems: (items: readonly HeaderOptionItem[]) => void;
}

/** รายการที่หน้าจอลงทะเบียนเข้าเมนู "ตัวเลือก" เช่น "Export Excel" */
export interface HeaderOptionItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly testId?: string;
}

export function useDesktopContext(): DesktopOutletContext {
  return useOutletContext<DesktopOutletContext>();
}

/** ไม่ทำอะไรเลย — ใช้เป็นค่าตั้งต้นเมื่อไม่มี `DesktopShell` ห่ออยู่จริง (ดู `useTrailingCrumbs`) */
function noopSetTrailingCrumbs(): void {
  // ไม่มี `DesktopShell` ให้บอก จึงไม่มีอะไรให้ทำ
}

/**
 * เวอร์ชันที่ใช้ได้แม้ไม่มี `<Outlet />` ห่ออยู่จริง เช่นเทสต์ระดับ component ที่
 * เรนเดอร์ `ProductMasterScreen` ตรง ๆ ใน `<MemoryRouter>` โดยไม่ผ่าน `DesktopShell`
 *
 * `useOutletContext` คืน `undefined` เงียบ ๆ เมื่อไม่มี provider (ไม่ throw) —
 * หน้าจอที่ destructure ค่านี้ตรง ๆ จะพังในบริบทเทสต์นั้นทั้งที่ตัวหน้าจอเองไม่ได้
 * ทำอะไรผิด ฟังก์ชันนี้จึงคืน no-op แทนเมื่อไม่มี context จริง — ปลอดภัยเพราะ
 * ผลลัพธ์เดียวของการไม่มี `DesktopShell` ห่ออยู่คือไม่มี breadcrumb ให้ต่อ ไม่ใช่
 * ผลลัพธ์ที่ผิดพลาดแบบเงียบ ๆ
 */
export function useTrailingCrumbs(): (crumbs: readonly Breadcrumb[]) => void {
  const context = useOutletContext<DesktopOutletContext | undefined>();
  return context?.setTrailingCrumbs ?? noopSetTrailingCrumbs;
}

/** ไม่ทำอะไรเลย — ใช้เป็นค่าตั้งต้นเมื่อไม่มี `DesktopShell` ห่ออยู่จริง (ดู `useHeaderOptionItems`) */
function noopSetHeaderOptionItems(): void {
  // ไม่มี `DesktopShell` ให้บอก จึงไม่มีอะไรให้ทำ
}

/** เวอร์ชันที่ใช้ได้แม้ไม่มี `<Outlet />` ห่ออยู่จริง — คืน no-op แทนการ throw (เหตุผลเดียวกับ `useTrailingCrumbs`) */
export function useHeaderOptionItems(): (items: readonly HeaderOptionItem[]) => void {
  const context = useOutletContext<DesktopOutletContext | undefined>();
  return context?.setHeaderOptionItems ?? noopSetHeaderOptionItems;
}
